import { message } from "antd";
import { ClassificationStatus } from "../utils";

export const useClassificationOperations = () => {
	const applyOperationsToTree = (tree, ids, operation) => {
		const processedIds = [];
	
		const processTree = (nodes) => {
			return nodes.map((node) => {
				const nodeUpdated = { ...node };
				let shouldProcessChildren = false;
	
				// Verificar si este nodo está en la lista de IDs seleccionados
				if (ids.includes(node.id)) {
					let canProcess = true;
	
					// Validaciones específicas para cada operación
					if (operation === "ARCHIVE") {
						if (nodeUpdated?.status === ClassificationStatus.NEW) {
							message.warning(
								`No puedes archivar la clasificación "${nodeUpdated.classification_name}" ya que fue recientemente creada`
							);
							canProcess = false;
						} else if (nodeUpdated?.isInUse) {
							// Si está en uso pero tiene hijos, podemos procesar los hijos
							if (node.children && node.children.length > 0) {
								shouldProcessChildren = true;
							}
						}
					} else if (operation === "UNARCHIVE") {
						if (!nodeUpdated?.archived) {
							message.warning(
								`No puedes desarchivar la clasificación "${nodeUpdated.classification_name}" ya que no está archivada`
							);
							canProcess = false;
						}
					}
	
					// Aplicar la operación si es posible
					if (canProcess) {
						if (operation === "ARCHIVE") {
							nodeUpdated.status = ClassificationStatus.ARCHIVED;							
						} else if (operation === "UNARCHIVE") {
							nodeUpdated.status = ClassificationStatus.UNARCHIVE;
						}
						processedIds.push(node.id);
						
						// También procesaremos todos los hijos
						shouldProcessChildren = true;
					}
				}
	
				if (node.children?.length > 0) {
					if (shouldProcessChildren) {
						// Si estamos procesando este nodo, forzar la operación en todos los hijos
						const allChildrenIds = getAllChildrenIds(node);
						const updatedChildren = processChildrenRecursively(node.children, operation);
						nodeUpdated.children = updatedChildren;
						
						// Añadir todos los IDs de los hijos procesados
						processedIds.push(...allChildrenIds.filter(id => {
							// Excluir elementos que ya estén en la lista
							return !processedIds.includes(id);
						}));
					} else {
						// Continuar el procesamiento normal para verificar si hay hijos seleccionados
						nodeUpdated.children = processTree(node.children);
					}
				}
				
				return nodeUpdated;
			});
		};
	
		// Función auxiliar para obtener todos los IDs de los hijos de un nodo
		const getAllChildrenIds = (node) => {
			let ids = [];
			if (!node.children || node.children.length === 0) return ids;
			
			node.children.forEach(child => {
				ids.push(child.id);
				ids = [...ids, ...getAllChildrenIds(child)];
			});
			
			return ids;
		};
	
		// Función para procesar todos los hijos recursivamente con la misma operación
		const processChildrenRecursively = (children, operation) => {
			return children.map(child => {
				const childUpdated = { ...child };
				let canProcess = true;
				
				// Validaciones específicas para cada operación en el hijo
				if (operation === "ARCHIVE") {
					if (childUpdated.status === ClassificationStatus.NEW) {
						canProcess = false;
					}
				} else if (operation === "UNARCHIVE") {
					if (!childUpdated.archived) {
						canProcess = false;
					}
				}
				
				if (canProcess) {
					if (operation === "ARCHIVE") {
						childUpdated.status = ClassificationStatus.ARCHIVED;						
					} else if (operation === "UNARCHIVE") {
						childUpdated.status = ClassificationStatus.UNARCHIVE;
					}
				}
				
				// Procesar recursivamente los hijos del hijo
				if (child.children?.length > 0) {
					childUpdated.children = processChildrenRecursively(child.children, operation);
				}
				
				return childUpdated;
			});
		};
	
		const updatedTree = processTree(tree);
	
		return { updatedTree, processedIds };
	};

	// Función para eliminar nodos por ID
	const removeNodesById = (nodes, idsToRemove) => {
		return nodes
			.filter((node) => !idsToRemove.includes(node.id))
			.map((node) => ({
				...node,
				children: node.children
					? removeNodesById(node.children, idsToRemove)
					: [],
				children_ids: (node.children_ids || []).filter(
					(id) => !idsToRemove.includes(id)
				),
			}));
	};

	// Función para aplanar clasificaciones
	const flattenClassifications = (tree, { includeIsInUse = false } = {}) => {
		const result = [];
		const traverse = (node) => {
			const {
				id,
				classification_name,
				classification,
				sort_order,
				depth,
				parent_id,
				children_ids,
				archived,
				isInUse,
				status = ClassificationStatus.NORMAL,
			} = node;

			const flattenedNode = {
				id,
				classification_name,
				classification,
				sort_order,
				depth,
				parent_id,
				children_ids,
				archived,
				status,
			};

			if (includeIsInUse) flattenedNode.isInUse = isInUse;
			result.push(flattenedNode);

			if (node.children && node.children.length > 0) {
				node.children.forEach(traverse);
			}
		};

		tree.forEach(traverse);
		return result;
	};

	// Función para limpiar datos después de guardar
	const cleanupData = (nodes, pendingOperations) => {
		const isInLatestOperation = (id, operationType) => {
			const relevantOps = pendingOperations
				.filter((op) => op.type === operationType)
				.sort((a, b) => b.timestamp - a.timestamp);

			if (relevantOps.length > 0) {
				return relevantOps.some((op) => op.ids && op.ids.includes(id));
			}
			return false;
		};

		return nodes.map((node) => {
			const {
				id,
				archived = false,
				children,
				status,
				...restNode
			} = node;
			let finalArchived = archived;

			if (isInLatestOperation(id, "ARCHIVE")) {
				finalArchived = true;
			} else if (isInLatestOperation(id, "UNARCHIVE")) {
				finalArchived = false;
			}

			if (status === ClassificationStatus.NEW) {
				finalArchived = false;
			}

			const updatedNode = {
				...restNode,
				id,
				archived: finalArchived,
				children: children
					? cleanupData(children, pendingOperations)
					: [],
			};

			return updatedNode;
		});
	};

	return {
		applyOperationsToTree,
		removeNodesById,
		flattenClassifications,
		cleanupData,
	};
};
